import { createFileRoute } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import {
  AgencyMemberDetailPage,
  DEFAULT_AGENCY_MEMBER_DETAIL_TAB,
  parseAgencyMemberDetailSearch,
  useAgencyMember,
  type AgencyMemberDetailTab,
} from '@/features/agency';
import { formatDocumentTitle } from '@/lib/document-title';

/**
 * `?tab=access|activity|account` selects the member detail section so links
 * and the back button land on it; `profile` is the default and never written.
 */
export const Route = createFileRoute('/_auth/agency/members/$memberId')({
  validateSearch: parseAgencyMemberDetailSearch,
  component: AgencyMemberDetailRoute,
});

function AgencyMemberDetailRoute() {
  const { memberId } = Route.useParams();
  const { tab = DEFAULT_AGENCY_MEMBER_DETAIL_TAB } = Route.useSearch();
  const navigate = Route.useNavigate();
  // Resolve from the shared members cache purely to title the tab; the page
  // itself handles loading / not-found states.
  const { member } = useAgencyMember(memberId);
  const name = member
    ? [member.firstName, member.lastName].filter(Boolean).join(' ').trim() || member.email
    : 'Member';

  const changeTab = (next: AgencyMemberDetailTab) =>
    void navigate({
      search: (prev) => ({ ...prev, tab: next === DEFAULT_AGENCY_MEMBER_DETAIL_TAB ? undefined : next }),
    });

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle(name)}</title>
      </Helmet>
      <AgencyMemberDetailPage memberId={memberId} tab={tab} onTabChange={changeTab} />
    </>
  );
}
